import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from 'react-redux';
import CustomModal from './customModal.jsx';
import axiosInstance from '../../../config/axiosApi/axiosApi.jsx';
import APIs from '../../../Constants/apis.js';
import { appSettings } from '../../../app/appSettingsSlice';

const ModifierSelectionModal = ({
  showModal,
  setShowModal,
  onSelect,
  onMultiSelect,
  onAddNew,
  compID,
  selectedModifier = null,
  isMultiSelect = false,
  excludeModifierIds = [],
  showAddNewButton = true,
  modalTitle = "Select Modifier",
  modalSubTitle = "Choose a modifier or add a new one",
  confirmButtonLabel,
}) => {
  const [modifiers, setModifiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(selectedModifier);
  const [selectedItems, setSelectedItems] = useState([]);
  const [compCurrency, setCompCurrency] = useState("GBP");

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Get profile data from Redux
  const { profileData } = useSelector((state) => state.auth);

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  

  // Fetch company currency
  const fetchCompCurrency = useCallback(async () => {
    try {
      // Use Redux state instead of API call
      if (!profileData) {
        setCompCurrency("GBP");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      console.error("Failed to load company currency:", err);
      setCompCurrency("GBP");
    }
  }, [profileData]);

  // Fetch modifiers when modal opens
  const fetchModifiers = useCallback(async () => {
    if (!compID) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/Item/GetAllModifiers', {
        params: {
          CompID: compID,
        },
      });

      const data = response.data.data || response.data;
      setModifiers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching modifiers:', error);
      setModifiers([]);
    } finally {
      setLoading(false);
    }
  }, [compID]);

  useEffect(() => {
    fetchCompCurrency();
  }, [fetchCompCurrency]);

  useEffect(() => {
    if (showModal) {
      fetchModifiers();
    }
  }, [showModal, fetchModifiers]);

  // No need for debounced search since we fetch all modifiers at once
  // Search is handled locally in the filteredModifiers calculation

  const excludedIdSet = useMemo(
    () => new Set((excludeModifierIds || []).map((id) => Number(id)).filter((id) => id > 0)),
    [excludeModifierIds]
  );

  const handleModifierClick = (modifier) => {
    if (isMultiSelect) {
      const id = Number(modifier.menuItem_ID);
      setSelectedItems((prev) => {
        const exists = prev.some((m) => Number(m.menuItem_ID) === id);
        if (exists) {
          return prev.filter((m) => Number(m.menuItem_ID) !== id);
        }
        return [...prev, modifier];
      });
      return;
    }
    setSelectedItem(modifier);
  };

  const handleSelect = () => {
    if (isMultiSelect) {
      if (selectedItems.length > 0 && onMultiSelect) {
        onMultiSelect(selectedItems);
        setShowModal(false);
        setSelectedItems([]);
        setSearchTerm('');
      }
      return;
    }
    if (selectedItem) {
      onSelect(selectedItem);
      setShowModal(false);
      setSelectedItem(null);
      setSearchTerm('');
    }
  };

  const handleAddNew = () => {
    if (onAddNew) {
      onAddNew();
    }
    setShowModal(false);
    setSelectedItem(null);
    setSelectedItems([]);
    setSearchTerm('');
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedItem(null);
    setSelectedItems([]);
    setSearchTerm('');
  };

  useEffect(() => {
    if (!showModal) {
      setSelectedItem(selectedModifier);
      setSelectedItems([]);
      setSearchTerm('');
    }
  }, [showModal, selectedModifier]);

  const availableModifiers = useMemo(
    () =>
      modifiers.filter(
        (modifier) => !excludedIdSet.has(Number(modifier.menuItem_ID))
      ),
    [modifiers, excludedIdSet]
  );

  const filteredModifiers = availableModifiers.filter(
    (modifier) =>
      modifier.menuItem_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      modifier.menuItem_Description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      modifier.category_Name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const emptyStateKind = useMemo(() => {
    if (filteredModifiers.length > 0) return null;
    if (searchTerm.trim()) return "search";
    if (modifiers.length === 0) return "library-empty";
    if (availableModifiers.length === 0) return "all-added";
    return "library-empty";
  }, [
    filteredModifiers.length,
    searchTerm,
    modifiers.length,
    availableModifiers.length,
  ]);

  const isModifierSelected = (modifier) => {
    const id = Number(modifier.menuItem_ID);
    if (isMultiSelect) {
      return selectedItems.some((m) => Number(m.menuItem_ID) === id);
    }
    return selectedItem?.menuItem_ID === modifier.menuItem_ID;
  };

  const modalLayoutHeight = "min(560px, calc(85vh - 140px))";

  return (
    <CustomModal
      isOpen={showModal}
      onClose={handleClose}
      title={modalTitle}
      subtitle={modalSubTitle}
      modalSize="modal-lg"
      showButton={false}
      isCentered={true}
      maxHeight="85vh"
      minHeight="420px"
      bodyOverflow="hidden"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: modalLayoutHeight,
          maxHeight: modalLayoutHeight,
          minHeight: "320px",
          padding: "4px 8px 0",
        }}
      >
        {/* Search — only useful when there is something to filter */}
        {modifiers.length > 0 ? (
        <div className="mb-3" style={{ flexShrink: 0 }}>
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, description, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={availableModifiers.length === 0}
              style={{
                borderRadius: "8px",
                border: `1px solid ${isDarkMode ? '#4a5568' : '#D9D9D9'}`,
                background: isDarkMode ? '#2d3748' : '#F9F9F9',
                color: isDarkMode ? '#e2e8f0' : '#495057',
                padding: "10px 40px 10px 15px",
                transition: 'all 0.2s ease',
                opacity: availableModifiers.length === 0 ? 0.55 : 1,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = themeColor;
                e.target.style.boxShadow = `0 0 0 3px ${themeColor}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? '#4a5568' : '#D9D9D9';
                e.target.style.boxShadow = 'none';
              }}
            />
            <i 
              className="fa fa-search position-absolute" 
              style={{ 
                right: "15px", 
                top: "50%", 
                transform: "translateY(-50%)", 
                color: isDarkMode ? '#a0aec0' : '#666' 
              }}
            />
            {searchTerm && (
              <button
                type="button"
                className="btn btn-sm position-absolute"
                style={{ 
                  right: "35px", 
                  top: "50%", 
                  transform: "translateY(-50%)", 
                  background: "none",
                  border: "none",
                  color: isDarkMode ? '#a0aec0' : '#666',
                  padding: "0",
                  fontSize: "12px"
                }}
                onClick={() => setSearchTerm('')}
                onMouseEnter={(e) => {
                  e.target.style.background = isDarkMode ? '#4a5568' : '#f8f9fa';
                  e.target.style.color = isDarkMode ? '#e2e8f0' : '#495057';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = isDarkMode ? '#a0aec0' : '#666';
                }}
              >
                <i className="fa fa-times"></i>
              </button>
            )}
          </div>
        </div>
        ) : null}

        {showAddNewButton && onAddNew && (
        <div className="mb-3" style={{ flexShrink: 0 }}>
          <button
            type="button"
            className="btn w-100"
            onClick={handleAddNew}
            style={{
              borderRadius: "8px",
              padding: "10px",
              fontSize: "14px",
              fontWeight: "500",
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: `1px solid ${themeColor}`,
              color: 'white',
              boxShadow: `0 4px 15px ${themeColor}40`,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 6px 20px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 4px 15px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus me-2"></i>
            Add New Modifier
          </button>
        </div>
        )}

        {/* Modifiers List — scrollable middle */}
        <div
          className="border rounded"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            border: `1px solid ${isDarkMode ? "#4a5568" : "#D9D9D9"}`,
            backgroundColor: isDarkMode ? "#2d3748" : "#ffffff",
          }}
        >
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status" style={{ color: themeColor }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2" style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>Loading modifiers...</p>
            </div>
          ) : filteredModifiers.length > 0 ? (
            <div className="p-2">
              {filteredModifiers.map((modifier, index) => {
                const selected = isModifierSelected(modifier);
                return (
                <div
                  key={modifier.menuItem_ID || index}
                  className="p-3 mb-2 rounded cursor-pointer transition-all"
                  onClick={() => handleModifierClick(modifier)}
                  style={{
                    border: selected
                      ? `2px solid ${themeColor}` 
                      : `1px solid ${isDarkMode ? '#4a5568' : '#D9D9D9'}`,
                    backgroundColor: selected
                      ? themeColor
                      : (isDarkMode ? '#4a5568' : '#ffffff'),
                    color: selected
                      ? 'white'
                      : (isDarkMode ? '#e2e8f0' : '#495057'),
                    cursor: "pointer",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    boxShadow: selected
                      ? `0 4px 12px ${themeColor}40`
                      : (isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)')
                  }}
                  onMouseEnter={(e) => {
                    if (!selected) {
                      e.currentTarget.style.backgroundColor = isDarkMode ? '#2d3748' : '#f8f9fa';
                      e.currentTarget.style.borderColor = themeColor;
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) {
                      e.currentTarget.style.backgroundColor = isDarkMode ? '#4a5568' : '#ffffff';
                      e.currentTarget.style.borderColor = isDarkMode ? '#4a5568' : '#D9D9D9';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      {isMultiSelect && (
                        <i
                          className={`fa ${selected ? 'fa-check-square' : 'fa-square-o'}`}
                          style={{ fontSize: "16px" }}
                        />
                      )}
                    <div>
                      <h6 className="mb-1" style={{ fontSize: "14px", fontWeight: "500" }}>
                        {modifier.menuItem_Name}
                      </h6>
                      <small style={{ 
                        fontSize: "11px",
                        color: selected
                          ? 'rgba(255,255,255,0.8)'
                          : (isDarkMode ? '#a0aec0' : '#6c757d')
                      }}>
                        Price: {getCurrencySymbol(compCurrency)}{modifier.menuItem_SalePrice || '0.00'}
                        {modifier.menuItem_Description && (
                          <span> | {modifier.menuItem_Description}</span>
                        )}
                      </small>
                    </div>
                    </div>
                    {selected && (
                      <i className="fa fa-check-circle text-white"></i>
                    )}
                  </div>
                </div>
              );})}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: "220px",
                padding: "24px 20px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "380px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    margin: "0 auto 16px",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: emptyStateKind === "all-added"
                      ? `${themeColor}22`
                      : isDarkMode
                        ? "rgba(148, 163, 184, 0.12)"
                        : "rgba(100, 116, 139, 0.1)",
                    border: `1px solid ${
                      emptyStateKind === "all-added"
                        ? `${themeColor}40`
                        : isDarkMode
                          ? "#4a5568"
                          : "#e2e8f0"
                    }`,
                  }}
                >
                  <i
                    className={`fa ${
                      emptyStateKind === "search"
                        ? "fa-search"
                        : emptyStateKind === "all-added"
                          ? "fa-check-circle"
                          : "fa-puzzle-piece"
                    }`}
                    style={{
                      fontSize: "26px",
                      color:
                        emptyStateKind === "all-added"
                          ? themeColor
                          : isDarkMode
                            ? "#94a3b8"
                            : "#64748b",
                    }}
                    aria-hidden="true"
                  />
                </div>

                <h6
                  style={{
                    margin: "0 0 6px",
                    fontSize: "15px",
                    fontWeight: 600,
                    color: isDarkMode ? "#f1f5f9" : "#0f172a",
                  }}
                >
                  {emptyStateKind === "search"
                    ? "No matches found"
                    : emptyStateKind === "all-added"
                      ? "All modifiers already added"
                      : "No modifiers in your library"}
                </h6>

                <p
                  style={{
                    margin: "0 0 18px",
                    fontSize: "13px",
                    lineHeight: 1.45,
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                  }}
                >
                  {emptyStateKind === "search"
                    ? `Nothing matched “${searchTerm.trim()}”. Try another name, description, or category.`
                    : emptyStateKind === "all-added"
                      ? "Every modifier from your library is already in this group. Create a new one on the group form if you need more."
                      : "Create modifiers first, then come back here to add them to this group. You can also add a new modifier directly on the group form."}
                </p>

                {emptyStateKind === "search" ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setSearchTerm("")}
                    style={{
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontSize: "13px",
                      fontWeight: 600,
                      backgroundColor: isDarkMode ? "#4a5568" : "#f1f5f9",
                      border: `1px solid ${isDarkMode ? "#4a5568" : "#e2e8f0"}`,
                      color: isDarkMode ? "#e2e8f0" : "#334155",
                    }}
                  >
                    <i className="fa fa-times me-2" aria-hidden="true" />
                    Clear search
                  </button>
                ) : showAddNewButton && onAddNew ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={handleAddNew}
                    style={{
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontSize: "13px",
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      border: `1px solid ${themeColor}`,
                      color: "#fff",
                      boxShadow: `0 4px 12px ${themeColor}40`,
                    }}
                  >
                    <i className="fa fa-plus me-2" aria-hidden="true" />
                    Create modifier
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn"
                    onClick={handleClose}
                    style={{
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontSize: "13px",
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      border: `1px solid ${themeColor}`,
                      color: "#fff",
                      boxShadow: `0 4px 12px ${themeColor}40`,
                    }}
                  >
                    <i className="fa fa-arrow-left me-2" aria-hidden="true" />
                    Back to group
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons — sticky footer */}
        <div
          className="d-flex gap-2"
          style={{
            flexShrink: 0,
            marginTop: "auto",
            paddingTop: "12px",
            borderTop: `1px solid ${isDarkMode ? "#4a5568" : "#dee2e6"}`,
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
          }}
        >
          <button
            type="button"
            className="btn flex-fill"
            onClick={handleClose}
            style={{
              borderRadius: "8px",
              padding: "10px",
              fontSize: "14px",
              backgroundColor: isDarkMode ? '#4a5568' : '#6c757d',
              border: `1px solid ${isDarkMode ? '#4a5568' : '#6c757d'}`,
              color: 'white',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#2d3748' : '#5a6268';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#4a5568' : '#6c757d';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn flex-fill"
            onClick={handleSelect}
            disabled={isMultiSelect ? selectedItems.length === 0 : !selectedItem}
            style={{
              borderRadius: "8px",
              padding: "10px",
              fontSize: "14px",
              backgroundColor: (isMultiSelect ? selectedItems.length === 0 : !selectedItem) ? '#C4C4C4' : themeColor,
              background: (isMultiSelect ? selectedItems.length === 0 : !selectedItem) ? '#C4C4C4' : `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: `1px solid ${(isMultiSelect ? selectedItems.length === 0 : !selectedItem) ? '#C4C4C4' : themeColor}`,
              color: 'white',
              cursor: (isMultiSelect ? selectedItems.length === 0 : !selectedItem) ? 'not-allowed' : 'pointer',
              opacity: (isMultiSelect ? selectedItems.length === 0 : !selectedItem) ? 0.6 : 1,
              transition: 'all 0.3s ease',
              boxShadow: (isMultiSelect ? selectedItems.length === 0 : !selectedItem) ? 'none' : `0 4px 15px ${themeColor}40`
            }}
            onMouseEnter={(e) => {
              const canSelect = isMultiSelect ? selectedItems.length > 0 : selectedItem;
              if (canSelect) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 6px 20px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              const canSelect = isMultiSelect ? selectedItems.length > 0 : selectedItem;
              if (canSelect) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 4px 15px ${themeColor}40`;
              }
            }}
          >
            {confirmButtonLabel ||
              (isMultiSelect
                ? `Add Selected (${selectedItems.length})`
                : "Select Modifier")}
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

export default ModifierSelectionModal;
